import React, { useState, useRef, useEffect } from 'react';
import { api } from '../api';

const MO_AVATAR = 'https://www.myfairmarketoffer.com/assets/images/mo-avatar.png';

const WELCOME = `Hey! I'm Mo, MFMO's rehab advisor. Tell me about this deal and I'll help you scope it out — what's the address and ARV? And what are we looking at for repairs?`;

export default function ChatPanel({ deal, onUpdate }) {
  const [open,        setOpen]        = useState(false);
  const [messages,    setMessages]    = useState([{ role: 'assistant', content: WELCOME }]);
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [lastUpdates, setLastUpdates] = useState([]);
  const [camUploading, setCamUploading] = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const cameraRef  = useRef(null);
  const histLoaded = useRef(false);

  // Load chat history from server when panel first opens
  useEffect(() => {
    if (!open || histLoaded.current) return;
    histLoaded.current = true;
    setHistLoading(true);
    api.getChatHistory(deal.id)
      .then(history => {
        if (history.length > 0) {
          // Replace welcome message with persisted history
          setMessages(history);
        }
        // else keep the welcome message
      })
      .catch(() => { /* silently keep welcome message on error */ })
      .finally(() => setHistLoading(false));
  }, [open, deal.id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg    = { role: 'user', content: text };
    const nextMsgs   = [...messages, userMsg];
    setMessages(nextMsgs);
    setInput('');
    setLoading(true);
    setLastUpdates([]);

    try {
      // Build API history: all messages, but must start with a user message
      const apiHistory = nextMsgs.filter(m => m.role === 'user' || m.role === 'assistant');
      // Drop leading assistant messages so history starts with user turn
      while (apiHistory.length > 0 && apiHistory[0].role === 'assistant') apiHistory.shift();
      const { reply, updates } = await api.chat(deal.id, apiHistory);

      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);

      if (updates?.length > 0) {
        setLastUpdates(updates);
        onUpdate(); // Refresh deal so P&L and fields reflect changes
      }
    } catch (err) {
      const errMsg = err.message?.includes('API key')
        ? "API key not configured. Ask Bryce to add ANTHROPIC_API_KEY to the server .env file."
        : "Something went wrong on my end — try again in a moment.";
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleCamera = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCamUploading(true);

    // Add user photo bubble immediately
    const objectUrl = URL.createObjectURL(file);
    setMessages(prev => [...prev, { role: 'user', content: '__photo__', photoUrl: objectUrl }]);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('label', 'Chat photo');
      const res = await fetch(`/api/deals/${deal.id}/photos/quick-upload`, { method: 'POST', body: formData });
      const data = await res.json();

      if (data.ok) {
        const driveMsg = data.driveFolderLink
          ? ` Saved to [Google Drive](${data.driveFolderLink}) 📁`
          : '';
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Got it — photo saved to your deal.${driveMsg} It'll show up in the Photos tab. Anything else you want to document?`,
        }]);
        onUpdate();
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Photo upload failed — try again.' }]);
    } finally {
      setCamUploading(false);
      e.target.value = '';
    }
  };

  return (
    <>
      {/* ── Floating Mo button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Ask Mo"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          width: 56, height: 56, borderRadius: '50%',
          padding: 0, border: `3px solid var(--brand)`,
          overflow: 'hidden', cursor: 'pointer',
          background: '#fff',
          boxShadow: open
            ? '0 4px 20px rgba(36,147,181,0.45)'
            : '0 4px 16px rgba(0,0,0,0.18)',
          transition: 'transform 0.15s, box-shadow 0.15s',
          transform: open ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        <img src={MO_AVATAR} alt="Mo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: 999,
          width: 368, height: 530,
          background: 'var(--card)',
          borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          border: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'moSlideUp 0.2s ease',
        }}>

          {/* Header */}
          <div style={{
            background: 'var(--brand)', color: '#fff',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            flexShrink: 0,
          }}>
            <img src={MO_AVATAR} alt="Mo"
              style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', objectFit: 'cover' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Mo</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>MFMO Rehab Assistant · Jacksonville</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'transparent', color: '#fff', fontSize: 18,
                padding: '2px 6px', opacity: 0.7, lineHeight: 1 }}
            >✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {histLoading && (
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', padding: '8px 0' }}>
                Loading conversation…
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex', gap: 8, alignItems: 'flex-end',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              }}>
                {msg.role === 'assistant' && (
                  <img src={MO_AVATAR} alt="Mo"
                    style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
                )}
                {msg.content === '__photo__' ? (
                  <img src={msg.photoUrl} alt="uploaded"
                    style={{ maxWidth: '78%', maxHeight: 180, borderRadius: '14px 14px 3px 14px', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    maxWidth: '78%',
                    padding: '9px 12px',
                    borderRadius: msg.role === 'user'
                      ? '14px 14px 3px 14px'
                      : '14px 14px 14px 3px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    background: msg.role === 'user' ? 'var(--brand)' : 'var(--bg)',
                    color: msg.role === 'user' ? '#fff' : 'var(--text)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {msg.content}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <img src={MO_AVATAR} alt="Mo"
                  style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }} />
                <div style={{
                  background: 'var(--bg)', padding: '10px 14px',
                  borderRadius: '14px 14px 14px 3px', fontSize: 13,
                }}>
                  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ animation: 'moDot 1.2s ease-in-out infinite',        width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)', display: 'inline-block' }} />
                    <span style={{ animation: 'moDot 1.2s ease-in-out 0.2s infinite',   width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)', display: 'inline-block' }} />
                    <span style={{ animation: 'moDot 1.2s ease-in-out 0.4s infinite',   width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)', display: 'inline-block' }} />
                  </span>
                </div>
              </div>
            )}

            {/* Update badges */}
            {lastUpdates.length > 0 && (
              <div style={{
                padding: '7px 10px',
                background: 'var(--brand-light)',
                borderRadius: 8, fontSize: 11,
                color: 'var(--brand)', lineHeight: 1.6,
                borderLeft: '3px solid var(--brand)',
              }}>
                ✓ {lastUpdates.join(' · ')}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input row */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid var(--border)',
            display: 'flex', gap: 8, flexShrink: 0,
            background: 'var(--card)',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Tell Mo about the deal…"
              rows={2}
              disabled={loading}
              style={{
                flex: 1, resize: 'none', fontSize: 13,
                padding: '8px 10px', borderRadius: 8,
                border: '1px solid var(--border)',
                fontFamily: 'inherit', lineHeight: 1.4,
                background: loading ? 'var(--bg)' : '#fff',
              }}
            />
            {/* Camera button */}
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={camUploading || loading}
              title="Upload a photo"
              style={{
                alignSelf: 'flex-end', flexShrink: 0,
                width: 38, height: 38, borderRadius: 8,
                background: 'var(--bg)', border: '1px solid var(--border)',
                fontSize: 18, cursor: 'pointer', padding: 0,
                opacity: camUploading ? 0.5 : 1,
              }}
            >
              {camUploading ? '⏳' : '📷'}
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              onChange={handleCamera} style={{ display: 'none' }} />

            <button
              className="btn-primary"
              onClick={send}
              disabled={!input.trim() || loading}
              style={{ alignSelf: 'flex-end', padding: '8px 14px', fontSize: 13, flexShrink: 0 }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes moSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes moDot {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40%            { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </>
  );
}
